import type { ProcedureType, RejectionGroundDto, RejectionGroundTextDto } from "../../autogen/bff-api";
import { isDpsScheme, isQualificationScheme } from "../procurement/procedure";
import { citesRegulation, isTenderRejectionGround } from "./rejectionGrounds";
import { getMarkdownFromTiptapContent } from "../input/TipTap/markdownUtils";

export const REJECTION_COMPLAINT_INFO =
  "Eventuell klage på avvisningen kan rettes til oppdragsgiver eller til Klagenemnda for offentlige anskaffelser (KOFA), Postboks 511 Sentrum, 5805 Bergen, eller per e-post post@kofa.no.";

export type RejectionVenue = "COMPETITION" | "DYNAMIC_PURCHASING_SYSTEM" | "QUALIFICATION_SCHEME";

const notAdmitted = (supplier: string, venue: RejectionVenue): string => {
  switch (venue) {
    case "DYNAMIC_PURCHASING_SYSTEM":
      return `Leverandøren ${supplier} tas ikke opp i den dynamiske innkjøpsordningen`;
    case "QUALIFICATION_SCHEME":
      return `Leverandøren ${supplier} tas ikke opp i kvalifikasjonsordningen`;
    case "COMPETITION":
      return `Leverandøren ${supplier} avvises fra konkurransen`;
  }
};

export const rejectionVenueOf = (procedureType: ProcedureType | null | undefined): RejectionVenue =>
  isDpsScheme(procedureType)
    ? "DYNAMIC_PURCHASING_SYSTEM"
    : isQualificationScheme(procedureType)
      ? "QUALIFICATION_SCHEME"
      : "COMPETITION";

export const supplierRejectionLead = (
  supplierName: string,
  grounds: RejectionGroundDto[],
  venue: RejectionVenue = "COMPETITION",
): string => {
  const supplier = supplierName.trim() || "leverandøren";
  const onlyTenderGrounds = grounds.length > 0 && grounds.every(isTenderRejectionGround);
  if (grounds.length > 0 && !grounds.some(citesRegulation)) {
    const rejected = onlyTenderGrounds
      ? `Tilbudet fra ${supplier} avvises.`
      : `${notAdmitted(supplier, venue)}.`;
    return `${rejected} Avvisningen bygger på anskaffelsesdokumentene og de grunnleggende prinsippene i anskaffelsesloven § 4, av følgende grunner:`;
  }
  return onlyTenderGrounds
    ? `Tilbudet fra ${supplier} avvises med hjemmel i følgende bestemmelser:`
    : `${notAdmitted(supplier, venue)} med hjemmel i følgende bestemmelser:`;
};

export const composeSupplierRejectionLetter = (
  supplierName: string,
  grounds: RejectionGroundDto[],
  groundTexts: RejectionGroundTextDto[],
  complaintBodyApplies: boolean,
  venue: RejectionVenue = "COMPETITION",
) => {
  const sections = grounds
    .map((ground) => {
      const message = (groundTexts.find((groundText) => groundText.key === ground.key)?.text ?? "").trim();

      const heading = citesRegulation(ground)
        ? [`**${ground.foaReference}**`, ground.label]
        : [`**${ground.label}**`];
      return [...heading, message]
        .filter(Boolean)
        .join("\n\n");
    })
    .join("\n\n");

  return [
    supplierRejectionLead(supplierName, grounds, venue),
    sections,
    complaintBodyApplies ? `**Klageadgang**\n\n${REJECTION_COMPLAINT_INFO}` : "",
  ]
    .filter(Boolean)
    .join("\n\n");
};

export const buildGroundlessRejectionMessageMarkdown = (
  supplierName: string,
  reason: string,
  rejectsTender: boolean,
  complaintBodyApplies = true,
  venue: RejectionVenue = "COMPETITION",
) => {
  const supplier = supplierName.trim() || "leverandøren";
  const rejected = rejectsTender ? `Tilbudet fra ${supplier} avvises.` : `${notAdmitted(supplier, venue)}.`;
  return [
    `${rejected} Avvisningen bygger på anskaffelsesdokumentene og de grunnleggende prinsippene i anskaffelsesloven § 4.`,
    reason.trim(),
    complaintBodyApplies ? `**Klageadgang**\n\n${REJECTION_COMPLAINT_INFO}` : "",
  ]
    .filter(Boolean)
    .join("\n\n");
};

export const getGroundMessageMarkdown = (content: string) =>
  getMarkdownFromTiptapContent(content).trim();

export interface PrivateRejectionMessageContext {
  eventTitle: string;
  supplierContactName?: string | null;
  responsibleBuyerName?: string | null;
  buyerOrganizationName: string;
}

export const buildPrivateRejectionMessageMarkdown = (
  context: PrivateRejectionMessageContext,
  reasonMarkdown: string,
): string => {
  const title = context.eventTitle.trim() || "konkurransen";
  const contact = context.supplierContactName?.trim();
  const responsible = context.responsibleBuyerName?.trim();
  return [
    `**Emne:** Avvisning av tilbud — ${title}`,
    `Hei${contact ? ` ${contact}` : ""},`,
    `Vi viser til tilbudet deres i ${title}.`,
    reasonMarkdown.trim(),
    "Tilbudet går derfor ikke videre i konkurransen. Vi takker for interessen og for arbeidet dere har lagt ned.",
    `Har dere spørsmål til beslutningen, kan dere svare på denne meldingen eller ta kontakt med ${
      responsible ?? "oss"
    }.`,
    ["Med vennlig hilsen", responsible, context.buyerOrganizationName.trim()].filter(Boolean).join("  \n"),
  ]
    .filter(Boolean)
    .join("\n\n");
};
